import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";
import { Button, Card, Alert } from "@/components/shared";
import { formatDate } from "@/utils/format";
import { buildPpraCertificateVerifyUrl } from "@/utils/ppra";
import { PPRAStatus, PPRAStatusLabels, ProvinceLabels, type Province } from "@/types";
import { CheckCircle, XCircle, Eye, MapPin, Building2, Clock, Phone, Hash, User, ChevronLeft, ChevronRight, ExternalLink, ShieldCheck } from "lucide-react";

interface QueueAgent {
  id: string;
  user_id: string;
  display_name: string;
  agency_name: string;
  ffc_number: string;
  ppra_status: PPRAStatus;
  years_experience: number | null;
  primary_region: Province;
  phone_number: string | null;
  ffc_expiry_date: string | null;
  created_at: string;
}

interface VerificationQueueProps {
  onSelectAgent?: (agentId: string) => void;
}

export function VerificationQueue({ onSelectAgent }: VerificationQueueProps) {
  const [agents, setAgents] = useState<QueueAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("submitted");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionAgent, setActionAgent] = useState<string | null>(null);
  const [autoVerifyingAgent, setAutoVerifyingAgent] = useState<string | null>(null);
  const [actionNotes, setActionNotes] = useState("");
  const [ffcExpiryDate, setFfcExpiryDate] = useState("");

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/management/verification-queue?status=${statusFilter}&page=${page}&limit=20`) as any;
      setAgents(res.data);
      setTotalPages(res.pagination.total_pages);
    } catch (e: any) {
      setError(e.message || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  async function handleAction(agentId: string, action: "approve" | "reject") {
    try {
      setNotice(null);
      await api.post(`/management/verify/${agentId}`, {
        action,
        notes: actionNotes || undefined,
        ffc_expiry_date: ffcExpiryDate || undefined,
      });
      setActionAgent(null);
      setActionNotes("");
      setFfcExpiryDate("");
      fetchQueue();
    } catch (e: any) {
      setError(e.message || "Verification action failed");
    }
  }

  async function handleAutoVerify(agentId: string) {
    setError(null);
    setNotice(null);
    setAutoVerifyingAgent(agentId);
    try {
      const result = await api.post<{
        ppra_status: PPRAStatus;
        auto_verification_status: "verified" | "not_found" | "needs_review";
        message?: string;
      }>(`/management/verify/${agentId}/auto`);

      if (result.auto_verification_status === "needs_review") {
        setNotice(result.message || "PPRA response needs manual review before changing the agent status.");
      } else {
        setNotice(
          result.auto_verification_status === "verified"
            ? "PPRA certificate verified automatically."
            : "PPRA did not return a clear match. Review this agent manually before changing status.",
        );
      }

      fetchQueue();
    } catch (e: any) {
      setError(e.message || "Automated verification failed");
    } finally {
      setAutoVerifyingAgent(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sun" />
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    submitted: "border-amber-500/40 bg-amber-500/5",
    verified: "border-green-500/40 bg-green-500/5",
    rejected: "border-red-500/40 bg-red-500/5",
  };

  return (
    <div className="space-y-6">
      {error && <Alert type="error">{error}</Alert>}
      {notice && <Alert type="info">{notice}</Alert>}

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        {["submitted", "verified", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wide transition-all duration-200 ${
              statusFilter === s
                ? "bg-gold/20 text-sun border border-gold shadow-[0_0_12px_rgba(214,168,46,0.15)]"
                : "text-white/50 border border-white/10 hover:border-white/25 hover:text-white/70"
            }`}
          >
            {PPRAStatusLabels[s as PPRAStatus] || s}
          </button>
        ))}
      </div>

      {/* Agent cards */}
      {agents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-white/30">
          <User className="h-12 w-12 mb-3 opacity-40" />
          <p className="text-sm">No agents with status &ldquo;{PPRAStatusLabels[statusFilter as PPRAStatus] || statusFilter}&rdquo;</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {agents.map((agent) => {
            const isSelected = actionAgent === agent.id;
            return (
              <div
                key={agent.id}
                className={`rounded-xl transition-all duration-300 ${isSelected ? "selected-card-border" : ""}`}
              >
                <div className={`rounded-xl border ${isSelected ? "border-transparent" : "border-white/10"} bg-white/[0.03] backdrop-blur-sm p-5 hover:bg-white/[0.05] transition-colors`}>
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="h-11 w-11 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-sun">
                          {agent.display_name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                        </span>
                      </div>
                      {/* Name + agency */}
                      <div>
                        <h3 className="text-white font-semibold text-base">{agent.display_name}</h3>
                        <p className="text-white/50 text-sm flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5" />
                          {agent.agency_name}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {agent.ppra_status === PPRAStatus.Submitted && (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={autoVerifyingAgent === agent.id}
                            onClick={() => handleAutoVerify(agent.id)}
                            disabled={autoVerifyingAgent !== null}
                            className="inline-flex items-center gap-1.5"
                            title="Call PPRA and update this verification automatically when the result is clear"
                          >
                            <ShieldCheck className="h-4 w-4" />
                            Auto verify
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setActionAgent(isSelected ? null : agent.id)}
                            className={isSelected ? "!bg-sun !text-ink" : ""}
                          >
                            {isSelected ? "Close" : "Review"}
                          </Button>
                        </>
                      )}
                      {onSelectAgent && (
                        <button
                          onClick={() => onSelectAgent(agent.id)}
                          className="text-white/30 hover:text-sun p-2 rounded-lg hover:bg-white/5 transition-colors"
                          title="View audit log"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Detail chips */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/60">
                      <Hash className="h-3 w-3 text-sun/70" />
                      <span className="font-mono">{agent.ffc_number || "—"}</span>
                    </span>
                    {agent.ffc_number && (
                      <a
                        href={buildPpraCertificateVerifyUrl(agent.ffc_number)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sun/10 border border-sun/25 text-xs text-sun hover:bg-sun/15 transition-colors"
                        title="Check PPRA certificate"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Check certificate
                      </a>
                    )}
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/60">
                      <MapPin className="h-3 w-3 text-sun/70" />
                      {ProvinceLabels[agent.primary_region]}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/60">
                      <Clock className="h-3 w-3 text-sun/70" />
                      {agent.years_experience ?? "—"} yrs experience
                    </span>
                    {agent.phone_number && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/60">
                        <Phone className="h-3 w-3 text-sun/70" />
                        {agent.phone_number}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/60">
                      Submitted {formatDate(agent.created_at)}
                    </span>
                  </div>

                  {/* Review panel (expanded) */}
                  {isSelected && (
                    <div className="mt-5 pt-5 border-t border-white/10 space-y-4 animate-[fadeIn_0.2s_ease-out]">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-white/40 mb-1.5">Verification Notes</label>
                          <textarea
                            value={actionNotes}
                            onChange={(e) => setActionNotes(e.target.value)}
                            rows={3}
                            maxLength={500}
                            className="input text-sm w-full"
                            placeholder="Add notes about this verification..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-white/40 mb-1.5">FFC Expiry Date</label>
                          <input
                            type="date"
                            value={ffcExpiryDate}
                            onChange={(e) => setFfcExpiryDate(e.target.value)}
                            className="input text-sm w-full"
                          />
                          <p className="mt-1.5 text-[11px] text-white/30">Set when approving to track certificate validity</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleAction(agent.id, "approve")}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600/20 border border-green-500/30 text-green-400 text-sm font-medium hover:bg-green-600/30 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4" /> Approve
                        </button>
                        <button
                          onClick={() => handleAction(agent.id, "reject")}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600/20 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-600/30 transition-colors"
                        >
                          <XCircle className="h-4 w-4" /> Reject
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="p-2 rounded-lg border border-white/10 text-white/50 hover:text-white hover:border-white/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-white/40 text-sm font-medium tabular-nums">
            {page} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            className="p-2 rounded-lg border border-white/10 text-white/50 hover:text-white hover:border-white/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
